import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ACTION_LINK } from "@core/components";
import { VerifyEmailScreen } from "@modules/account/views";
import { getPendingRound } from "@modules/applications/_shared";

interface VerifyEmailSearch {
  /** The challenge token from the emailed link. Absent/malformed renders the invalid state. */
  token?: string;
}

/**
 * `/verify-email` — the landing path for the verification email's link.
 *
 * The path is NOT a choice: the backend builds the link as
 * `{IDENTITY_PUBLIC_BASE_URL}/verify-email?token=…` (`identity/services.py`), so this route has to
 * match it exactly or every verification email dead-ends.
 *
 * PUBLIC, deliberately — no `beforeLoad`. The link is opened wherever the applicant reads their
 * mail, frequently on a device with no session, and confirmation itself needs no auth. A guard here
 * would lock out exactly the people the email was sent to.
 *
 * ## Where "continue" goes, and why the route decides it
 *
 * Confirming the address is what creates the dossier — but not here. This screen cannot: the link
 * carries only a token, `confirm-link/` returns no session, and the mail may be open on a phone that
 * has never signed in. So it hands the applicant to `/apply/{roundId}`, which has both a session
 * (its guard proves it) and the round (in its path), and that route does the create.
 *
 * The round comes from the parked value written at registration — the only way it survives a URL the
 * backend owns. When nothing is parked (another device, cleared storage, or someone verifying
 * outside the apply funnel) the screen falls back to its own surface-home link, which is correct
 * rather than merely safe: they are verified either way, and re-entering through the catalogue
 * reaches the same create.
 */
export const Route = createFileRoute("/verify-email")({
  validateSearch: (search: Record<string, unknown>): VerifyEmailSearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: VerifyEmailRoute,
});

function VerifyEmailRoute() {
  const { token } = Route.useSearch();
  const { t } = useTranslation("account");

  /**
   * Read once per render rather than in an effect: this is a synchronous `localStorage` read of a
   * public UUID, and it decides what to render, so there is nothing to synchronise.
   *
   * NOT cleared here. The applicant may verify and close the tab before the redirect lands, and
   * dropping the round at this point would strand the next visit. `/apply/{roundId}` clears it once
   * the dossier actually exists.
   */
  const pendingRoundId = getPendingRound();

  return (
    <VerifyEmailScreen
      // Normalized to "" rather than passed as undefined: the screen treats an empty token as the
      // invalid state and skips the request entirely, so there is one code path for "no usable
      // token".
      token={token ?? ""}
      continueAction={
        pendingRoundId ? (
          <Link
            to="/apply/$roundId"
            params={{ roundId: pendingRoundId }}
            className={ACTION_LINK}
          >
            {t("verification.verified.continue")}
          </Link>
        ) : undefined
      }
    />
  );
}
